import { useState, useEffect } from 'react';
import { normalizeCurrencyString } from 'core/utilities';

export type FormValues = {
    // Salary
    salary : string;
    has_investment_properties : boolean;

    // Investment property
    full_address : string;
    address : string;
    suite : string;
    city : string;
    region : string;
    country : string;
    postal : string;
    investment_property_value : string;
    investment_property_balance_owing : string;
    investment_property_monthly_mortgage_payments : string;
    investment_property_monthly_property_taxes : string;

    // Investment accounts
    non_reg_cash : string;
    rrsp : string;
    tfsa : string;
    resp : string;

    // Mortgage 
    property_value : string;
    mortgage_balance : string;
    monthly_mortgage : string;
    property_tax : string;

    // Credit card debt
    credit_debt_amount : string;
    credit_debt_monthly_payment : string;

    // Lines of credit
    credit_line_amount : string;
    credit_line_monthly_payment : string;

    // Other debt
    other_debt_amount : string;
    other_debt_monthly_payment : string;

    // Car debt
    car_debt_amount : string;
    car_debt_monthly_payment : string;
};

export const useInitialValues = (complete : any) =>
{
    const { applicants, debts, investments, user_address } = complete;
    const applicant = applicants && applicants.length > 0 ? applicants[0] : {};
    const investment = investments && investments.length > 0 ? investments : [];
    const debt = debts && debts.length > 0 ? debts : [];

    // Every item in `investment` with account_type === 'p' is an investment property
    // `has_investment_property` true if at least one of the above exists
    // investmentPropertyId = investment[i].properties.id;
    // Item `d` in `debt` `d.source` === 'm' && `d.investment` === `investmentProperty.id` is `investmentProperty.debtId`
    const [ investmentProperty, setInvestmentProperty ] = useState<{ id? : string; propertyId? : string; debtId? : string; }>({});
    const [ debtIDs, setDebtIDs ] = useState<{ [ key : string ] : any; }>({});
    const [ initialValues, setInitialValues ] = useState<FormValues>({
        // Salary
        salary : (applicant.salary && applicant.salary >= 0) ? normalizeCurrencyString(applicant.salary) : '',
        has_investment_properties : investmentProperty.id !== undefined,

        // Investment property
        full_address : '',
        address : '',
        suite : '',
        city : '',
        region : '',
        country : '',
        postal : '',
        investment_property_value : '',
        investment_property_balance_owing : '',
        investment_property_monthly_mortgage_payments : '',
        investment_property_monthly_property_taxes : '',

        // Investment accounts
        non_reg_cash : '$0',
        rrsp : '$0',
        tfsa : '$0',
        resp : '$0',

        // Mortgage
        property_value : user_address.property_value ? normalizeCurrencyString(user_address.property_value) : '',
        mortgage_balance : user_address.mortgage_balance ? normalizeCurrencyString(user_address.mortgage_balance) : '',
        monthly_mortgage : user_address.monthly_mortgage ? normalizeCurrencyString(user_address.monthly_mortgage) : '',
        property_tax : user_address.property_tax ? normalizeCurrencyString(user_address.property_tax) : '',

        // Credit debt
        credit_debt_amount : '$0',
        credit_debt_monthly_payment : '$0',

        // Lines of credit
        credit_line_amount : '$0',
        credit_line_monthly_payment : '$0',

        // Other debt
        other_debt_amount : '$0',
        other_debt_monthly_payment : '$0',

        // Car debt
        car_debt_amount : '$0',
        car_debt_monthly_payment : '$0',
    });

    useEffect(() =>
    {
        const investAccountCodeMap = {
            non_reg_cash : 'c',
            rrsp : 'r',
            tfsa : 't',
            resp : 'e',
        };

        const accountCodeToFieldname = {
            c : 'non_reg_cash',
            r : 'rrsp',
            t : 'tfsa',
            e : 'resp'
        };
        
        for (const inv of investment)
        {
            if (inv.account_type === 'p')
            {
                const { id, properties = {}, amount: propertyAmount } = inv;
                for (const d of debt)
                {
                    const { id : debtId, source, investment, amount, monthly_payment } = d;
                    if (source === 'm' && investment === id)
                    {
                        setInvestmentProperty({
                            id,
                            propertyId : properties.id,
                            debtId : debtId
                        });

                        const { 
                            monthly_property_tax,
                            address,
                            suite,
                            city,
                            region,
                            country,
                            postal
                        } = properties;

                        const property_tax = monthly_property_tax ? normalizeCurrencyString(monthly_property_tax) : '$0';
                        const amount_owing = amount ? normalizeCurrencyString(amount) : '$0';
                        const payment = monthly_payment ? normalizeCurrencyString(monthly_payment) : '$0';
                        const estimatedValue = propertyAmount ? normalizeCurrencyString(propertyAmount) : '$0';

                        setInitialValues(values => ({
                            ...values,
                            full_address: address,
                            address,
                            suite,
                            city,
                            region,
                            country,
                            postal,
                            has_investment_properties : true,
                            investment_property_monthly_property_taxes : property_tax,
                            investment_property_balance_owing : amount_owing,
                            investment_property_monthly_mortgage_payments : payment,
                            investment_property_value : estimatedValue
                        }));

                        break;
                    }
                }
                break;
            }
            else if (Object.values(investAccountCodeMap).includes(inv.account_type))
            {
                // @ts-ignore
                const fieldname = accountCodeToFieldname[inv.account_type];
                setInitialValues(values => ({
                    ...values,
                    [fieldname] : normalizeCurrencyString(inv.amount)
                }));
            }
        }

        for (const d of debt)
        {
            if (d.source === 'a') // Car loan
            {
                setDebtIDs(ids => ({ ...ids, a : d.id }));
                setInitialValues(values => ({
                    ...values,
                    car_debt_amount : normalizeCurrencyString(d.amount),
                    car_debt_monthly_payment : normalizeCurrencyString(d.monthly_payment)
                }));
            }
            else if (d.source === 'c') // Credit card
            {
                setDebtIDs(ids => ({ ...ids, c : d.id }));
                setInitialValues(values => ({
                    ...values,
                    credit_debt_amount : normalizeCurrencyString(d.amount),
                    credit_debt_monthly_payment : normalizeCurrencyString(d.monthly_payment)
                }));
            }
            else if (d.source === 'l') // Personal
            {
                setDebtIDs(ids => ({ ...ids, l : d.id }));
                setInitialValues(values => ({
                    ...values,
                    credit_line_amount : normalizeCurrencyString(d.amount),
                    credit_line_monthly_payment : normalizeCurrencyString(d.monthly_payment)
                }));
            }
            else if (d.source === 'o') // Other
            {
                setDebtIDs(ids => ({ ...ids, o : d.id }));
                setInitialValues(values => ({
                    ...values,
                    other_debt_amount : normalizeCurrencyString(d.amount),
                    other_debt_monthly_payment : normalizeCurrencyString(d.monthly_payment)
                }));
            }
        }
    }, []);

    return {
        applicant,
        investment,
        investmentProperty,
        user_address,
        debtIDs,
        initialValues,
        setInitialValues
    };
};